import {
    CodeBlock,
    BlockType,
    SettingType,
    ValidationMessage,
    SettingState,
    TibboDataType,
    Define,
    ValidationMessageType,
    SettingControlType,
} from '@appblocks/codegeneration/types';

import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import LUISGroup from '@appblocks/codegeneration/SettingsConfigurations/LUISGroup';
import PlatformUtils, {
    WEB_ADMIN_WIDGET_COLUMNS,
    WEB_ADMIN_WIDGET_ROWS,
} from '@appblocks/platforms/PlatformUtils';
import * as micropython from './micropython';
import * as tios from './tios';
import * as zephyr from './zephyr';

export default class WebDashboard extends BaseFeature {
    constructor() {
        super();
        this.name = 'Web Console';
        this.id = 'web_dashboard';
        this.icon = 'MdOutlineSpaceDashboard';
        this.requirements = ['network'];

        const settings = [
            {
                id: 'GENERAL',
                name: 'General',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'SETTINGS',
                name: 'Settings',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'ADVANCED',
                name: 'Advanced',
                settingType: SettingType.GROUP,
                default: '',
            },
            {
                id: 'ENABLED',
                name: 'Web Console Enabled',
                settingType: SettingType.OPTION,
                group: 'GENERAL',
                required: true,
                default: '0',
                helpText: 'Enable this will enable editing of data tables, settings, and variables via web browser',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'TABLESENABLED',
                name: 'Table Editing Page',
                settingType: SettingType.HIDDEN,
                required: true,
                group: 'ADVANCED',
                default: '1',
                readonly: true,
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'SETTINGSENABLED',
                name: 'Setting Editing Page',
                settingType: SettingType.HIDDEN,
                group: 'ADVANCED',
                required: true,
                default: '1',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'WIDGETENABLED',
                name: 'Dashboard Page',
                settingType: SettingType.HIDDEN,
                required: true,
                group: 'ADVANCED',
                default: '1',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'INTERVAL',
                name: 'Dashboard Refresh (seconds)',
                settingType: SettingType.NUMBER,
                required: true,
                group: 'ADVANCED',
                min: 1,
                max: 300,
                default: '1',
            },
            {
                id: 'DASHBOARD_TITLE',
                name: 'Title',
                settingType: SettingType.STRING,
                required: true,
                group: 'ADVANCED',
                default: 'Device Console',
            },
            {
                id: 'WIDGETS',
                name: 'Dashboard Widgets',
                settingType: SettingType.ARRAY,
                controlType: SettingControlType.WIDGETS,
                required: true,
                default: new Array<BaseFeature>(0),
            },
            {
                id: 'USER',
                name: 'Username',
                settingType: SettingType.STRING,
                group: 'ADVANCED',
                default: 'admin',
            },
            {
                id: 'PASSWORD',
                name: 'Password',
                settingType: SettingType.PASSWORD,
                dataType: TibboDataType.SETTING,
                setting: {
                    SETTINGID: 'WBPWD',
                    NAME: 'Web Password',
                    SETTINGTYPE: 'string',
                    DATATYPE: 'S',
                    MAX: 32,
                },
                UIControl: {
                    CONTROL: 'EDIT',
                },
                group: 'ADVANCED',
                default: '',
            },
            {
                id: 'GROUPS',
                name: 'Groups',
                settingType: SettingType.ARRAY,
                refId: 'luis_group',
                required: true,
                controlType: SettingControlType.TABS,
                default: [
                    new LUISGroup(),
                ],
            },
            {
                id: 'WEB_ADMIN_WIDGET_COLUMNS',
                name: 'Widget Columns',
                settingType: SettingType.HIDDEN,
                required: true,
                default: WEB_ADMIN_WIDGET_COLUMNS.toString(),
            },
            {
                id: 'WEB_ADMIN_WIDGET_ROWS',
                name: 'Widget Rows',
                settingType: SettingType.HIDDEN,
                required: true,
                default: WEB_ADMIN_WIDGET_ROWS.toString(),
            },
            {
                id: 'CONSOLE_PREVIEW',
                name: 'Console Preview',
                settingType: SettingType.CONSOLE_PREVIEW,
                group: 'GENERAL',
                default: 'device',
            },
            {
                id: 'TABLES',
                name: 'Tables',
                settingType: SettingType.ARRAY,
                refId: 'console_table_item',
                required: true,
                default: new Array<BaseFeature>(0),
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getFiles(): string[] {
        const files = [
            'console_Main.js.gz',
            // 'Main.css.gz',
            'dashboard_setup.json',
        ];
        if (this.getSettingValue('WIDGETS').length > '1') {
            files.push('console_Dashboard.js.gz');
        }
        if (this.project.runtime === 'zephyr') {
            return zephyr.getFiles(this);
        }

        const widgets = this.getSettingValue('WIDGETS');
        if (widgets.length > 0) {
            files.push('console_Dashboard.js.gz');
        }

        return files;
    }

    getSettingsEnabled() {
        const GROUPS = this.getSettingValue('GROUPS');
        let settingsCount = 0;
        for (let i = 0; i < GROUPS.length; i += 1) {
            const group = GROUPS[i];
            settingsCount += group.getSettingValue('SETTINGS').length;
        }
        return settingsCount > 0;
    }

    getDependencies() {
        const dependencies = [
            'httpserver',
            'stgcmd_lib',
        ];
        if (this.getSettingsEnabled()) {
            dependencies.push('stg');
        }
        if (PlatformUtils.getCapabilities(this.project).includes('TPP')) {
            dependencies.push('firmware_upgrade');
        }
        return dependencies;
    }

    getDefines(): Define[] {
        return [];
    }

    getState(settingId: string): SettingState {
        if (!this.getEnabled() && settingId !== 'ENABLED') {
            return SettingState.HIDDEN;
        }
        switch (settingId) {
            case 'ENABLED':
                return SettingState.ENABLED;
            case 'USER':
                return SettingState.DISABLED;
            default:
                return SettingState.ENABLED;
        }
    }

    validate(): Array<ValidationMessage> {
        const errors: ValidationMessage[] = [];
        const widgetColumns = parseInt(this.getSettingValue('WEB_ADMIN_WIDGET_COLUMNS'), 10);
        const widgetRows = parseInt(this.getSettingValue('WEB_ADMIN_WIDGET_ROWS'), 10);
        if (widgetColumns !== WEB_ADMIN_WIDGET_COLUMNS
            || widgetRows !== WEB_ADMIN_WIDGET_ROWS) {
            const widgets = this.getSettingValue('WIDGETS');
            const rowsMultiplier = WEB_ADMIN_WIDGET_ROWS / widgetRows;
            const columnsMultiplier = WEB_ADMIN_WIDGET_COLUMNS / widgetColumns;
            for (let i = 0; i < widgets.length; i++) {
                const widget = widgets[i];
                const widgetX = widget.getSettingValue('X');
                const widgetY = widget.getSettingValue('Y');
                const widgetWidth = widget.getSettingValue('W');
                const widgetHeight = widget.getSettingValue('H');
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: 'Widget',
                    featureId: widget.getID(),
                    settingId: 'X',
                    data: Math.floor(widgetX * columnsMultiplier).toString(),
                });
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: 'Widget',
                    featureId: widget.getID(),
                    settingId: 'Y',
                    data: Math.floor(widgetY * rowsMultiplier).toString(),
                });
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: 'Widget',
                    featureId: widget.getID(),
                    settingId: 'W',
                    data: Math.floor(widgetWidth * columnsMultiplier).toString(),
                });
                errors.push({
                    messageType: ValidationMessageType.UPDATE,
                    message: 'Widget',
                    featureId: widget.getID(),
                    settingId: 'H',
                    data: Math.floor(widgetHeight * rowsMultiplier).toString(),
                });
            }
            errors.push({
                messageType: ValidationMessageType.UPDATE,
                message: 'Widget',
                featureId: this.getID(),
                settingId: 'WEB_ADMIN_WIDGET_COLUMNS',
                data: WEB_ADMIN_WIDGET_COLUMNS.toString(),
            });
            errors.push({
                messageType: ValidationMessageType.UPDATE,
                message: 'Widget',
                featureId: this.getID(),
                settingId: 'WEB_ADMIN_WIDGET_ROWS',
                data: WEB_ADMIN_WIDGET_ROWS.toString(),
            });
        }
        return errors;
    }

    getCodeBlocks(): CodeBlock[] {
        switch (this.project.runtime) {
            case 'micropython':
                return micropython.getCodeBlocks(this);
            case 'zephyr':
                return zephyr.getCodeBlocks(this);
            case 'tios':
                return tios.getCodeBlocks(this);
            default:
                return [];
        }
    }

    getDetails(): string {
        return `
### Web Dashboard to expose variables in graphs and charts
![Web Dashboard](/static/img/dashboard_screenshot.png "Title")

`;
    }
}
